import { useCallback, useRef, useState } from "react";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";

import { PasskeyOffer } from "./components/PasskeyOffer.js";
import { TabBar } from "./components/TabBar.js";
import { useOffline } from "./lib/connection.js";
import { useIsDesktop } from "./lib/media.js";
import { SessionGate } from "./lib/session.js";
import { Desktop } from "./screens/desktop/Desktop.js";
import { Display } from "./screens/display/Display.js";
import { Day } from "./screens/Day.js";
import { Editor } from "./screens/Editor.js";
import { EventDetail } from "./screens/EventDetail.js";
import { Month } from "./screens/Month.js";
import { Settings } from "./screens/Settings.js";
import { ImportSkylight } from "./screens/ImportSkylight.js";
import { SignIn } from "./screens/SignIn.js";
import { Week } from "./screens/Week.js";

interface Toast {
  message: string;
  undo?: (() => Promise<void>) | undefined;
}

const TOAST_MS = 4200;

function Shell(): React.JSX.Element {
  const { pathname } = useLocation();
  const isDesktop = useIsDesktop();
  const offline = useOffline();
  const [toast, setToast] = useState<Toast | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flash = useCallback((message: string, undo?: () => Promise<void>) => {
    setToast({ message, undo });
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = setTimeout(() => setToast(null), TOAST_MS);
  }, []);

  // The editor is a sheet over the app, so the tab bar goes away with it.
  // The desktop carries its own chrome; the tab bar is a phone affordance.
  const isSheet = pathname.startsWith("/event/");
  const showTabBar = !isSheet && !isDesktop;

  return (
    <>
      {/* Showing old data is fine. Showing old data while looking current is
          not — the same rule the kitchen screen is built on. */}
      {offline && (
        <div className="offline" role="status">
          Offline — showing what was here last
        </div>
      )}

      <Routes>
        <Route path="/" element={isDesktop ? <Desktop initial="month" /> : <Week />} />
        <Route path="/month" element={isDesktop ? <Desktop initial="month" /> : <Month />} />
        <Route path="/day/:date" element={<Day />} />
        {/*
          Tapping an event asks a question before it proposes a change, so
          /event/:id reads and /event/:id/edit writes. Every call site in the
          app already pointed at the first of those, which is why they all
          keep working and now land somewhere gentler. "new" is the exception:
          there is nothing to read yet.
        */}
        <Route path="/event/new" element={<Editor onToast={flash} />} />
        <Route path="/event/:id" element={<EventDetail />} />
        <Route path="/event/:id/edit" element={<Editor onToast={flash} />} />
        <Route path="/settings" element={<Settings />} />
        {/*
          Reachable by address only: the import ran once and its section came
          off the settings screen, because a one-time migration on permanent
          offer is one somebody eventually runs twice. The route stays because
          the screen is also where imported events are removed again.
        */}
        <Route path="/settings/import" element={<ImportSkylight />} />
        <Route path="*" element={isDesktop ? <Desktop initial="month" /> : <Week />} />
      </Routes>

      {showTabBar && <TabBar />}

      <PasskeyOffer />

      {toast !== null && (
        <div className="toast" role="status">
          <span>{toast.message}</span>
          {toast.undo !== undefined && (
            <button
              type="button"
              className="toast__undo"
              onClick={() => {
                const undo = toast.undo;
                setToast(null);
                if (undo !== undefined) void undo();
              }}
            >
              Undo
            </button>
          )}
        </div>
      )}
    </>
  );
}

export function App(): React.JSX.Element {
  return (
    <BrowserRouter>
      <Routes>
        {/* The kitchen screen never signs in — its token is the credential —
            so it sits outside the session gate entirely. */}
        <Route path="/display" element={<Display />} />
        <Route
          path="*"
          element={
            <SessionGate signedOut={<SignIn />}>
              <Shell />
            </SessionGate>
          }
        />
      </Routes>
    </BrowserRouter>
  );
}
